import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import {
  AcknowledgementDetailsResponse,
  FeeSplitFeeTypeEnum,
  MemberFeeSplit,
} from '../../../openapi/yenta';
import { formatCurrency } from '../../../utils/currencyUtils';
import Hover from '../../Hover';

interface TeamAgreementProps {
  teamInvitationData?: AcknowledgementDetailsResponse | null;
}

// Reusable Card Component
const TeamAgreementCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className='bg-real-blue-50 border border-real-blue-200 rounded-2xl p-4'>
      {children}
    </div>
  );
};

// Tooltip Info Icon Component
const TooltipIcon: React.FC<{ content: React.ReactNode }> = ({ content }) => {
  return (
    <Hover
      hoverComponent={
        <div className='p-2 text-sm font-inter max-w-xs'>{content}</div>
      }
      config={{ trigger: 'click', placement: 'auto' }}
      variant='light'
    >
      <FontAwesomeIcon
        icon={faCircleInfo}
        className='text-real-blue-500 text-xs cursor-pointer'
      />
    </Hover>
  );
};

// Tooltip Content
const TOOLTIP_CONTENT = {
  TEAMMATE_REAL_CAP: (
    <>
      Real fees are charges that are collected only when you close transactions.
      Real does not charge any monthly fees.
    </>
  ),
  REAL_SPLIT: (
    <>
      Real fees are charges that are collected only when you close transactions.
      Real does not charge any monthly fees.
    </>
  ),
  CBR_FEE: (
    <>
      A <strong>$40 CBR fee</strong> per transaction, which covers broker
      review, E&O (professional liability) insurance, and processing will be
      assessed on every transaction in the United States the company processes.
    </>
  ),
  BROKERAGE_FEE: (
    <>
      Real charges a <strong>$750 annual brokerage fee</strong>. The Brokerage
      Fee is collected from the first three transactions each anniversary year.
    </>
  ),
  POST_CAP_TRX: (
    <>
      A Post Cap Transaction Fee is a fee that is assessed by Real Brokerage on
      each transaction after an agent has reached their annual cap. The fee is{' '}
      <strong>$285</strong> per transaction, up to $6,000 per year, and is split
      between all agents on a transaction in proportion to the percentage of
      commission each agent earns. However, if an agent is considered an Elite
      Agent, the Post Cap Transaction Fee is reduced to <strong>$129</strong>{' '}
      per transaction.
    </>
  ),
  LEADER_SPLIT: (
    <>
      The Leader&apos;s Split is the percentage amount that is automatically
      deducted from the Teammate&apos;s gross amount and credited to the Team
      Leader. When there are multiple Team Leaders, each can have their own
      split.
    </>
  ),
  TEAM_CAP: (
    <>
      If the Teammate is configured to pay a percentage Team Leader&apos;s split
      from each transaction, the administrator can optionally set a maximum Team
      Cap limit for the anniversary cycle. After Team Cap is reached, the
      Leader&apos;s Split will no longer be charged until the end of the
      anniversary year.
    </>
  ),
  TEAM_PRE_CAP: (
    <>
      The administrator can optionally configure the team to automatically apply
      a flat pre-cap fee (dollar amount). In that case, the Teammate will see a
      Team Pre Cap fee deduction on each transaction until the Team Cap is
      reached.
    </>
  ),
  TEAM_POST_CAP: (
    <>
      The administrator can optionally configure the team to automatically apply
      a flat post-cap fee (dollar amount). In that case, the Teammate will see a
      Team Post Cap fee deduction on each transaction after the Team Cap is
      reached. If the Team Cap is not specified, this fee will never be applied.
    </>
  ),
};

// Helper functions moved outside component to avoid recreation on each render
const formatPercent = (value?: number | null): string => {
  if (value === null || value === undefined) return '0%';
  // If value is already a percentage (0-100), just add %
  if (value > 1) return `${value}%`;
  // If value is a decimal (0-1), multiply by 100
  return `${(value * 100).toFixed(2)}%`;
};

const calculateIndividualFee = (
  memberFeeSplits: MemberFeeSplit[] | undefined,
  feeType: FeeSplitFeeTypeEnum,
): number => {
  if (!memberFeeSplits) return 100;

  let totalLeaderFee = 0;
  memberFeeSplits.forEach((split) => {
    const fee = split.feeSplits?.find((f) => f.feeType === feeType);
    if (fee) {
      totalLeaderFee += fee.percent || 0;
    }
  });

  return Math.max(0, 100 - totalLeaderFee);
};

/**
 * TeamAgreement component displays team fee structure and leader fees
 * Uses compensation plan data directly from backend
 */
const TeamAgreement: React.FC<TeamAgreementProps> = ({
  teamInvitationData,
}) => {
  // Calculate display values from API data during render
  const { individualFees, leaders, teammateCap, teamFees } = useMemo(() => {
    if (!teamInvitationData) {
      return {
        individualFees: {
          split: '0%',
          cbr: '0%',
          brokerage: '0%',
          postCapTransaction: '0%',
        },
        leaders: [],
        teammateCap: null,
        teamFees: null,
      };
    }

    // Calculate individual fees on the fly
    const calculatedIndividualFees = {
      split: formatPercent(
        calculateIndividualFee(
          teamInvitationData.memberFeeSplits,
          FeeSplitFeeTypeEnum.RealSplit,
        ),
      ),
      cbr: formatPercent(
        calculateIndividualFee(
          teamInvitationData.memberFeeSplits,
          FeeSplitFeeTypeEnum.CbrFee,
        ),
      ),
      brokerage: formatPercent(
        calculateIndividualFee(
          teamInvitationData.memberFeeSplits,
          FeeSplitFeeTypeEnum.BrokerageFee,
        ),
      ),
      postCapTransaction: formatPercent(
        calculateIndividualFee(
          teamInvitationData.memberFeeSplits,
          FeeSplitFeeTypeEnum.TransactionFee,
        ),
      ),
    };

    // Map leaders - use fullName directly from API, filter out 0 splits
    const calculatedLeaders =
      teamInvitationData.leaderSplits
        ?.filter((split) => split.leaderSplit !== 0)
        .map((split) => {
          return {
            id: split.leaderId || '',
            name: split.fullName || 'Unknown Leader',
            splitPercent: formatPercent(split.leaderSplit),
          };
        }) || [];

    // Calculate teammate's real cap
    const calculatedTeammateCap = teamInvitationData.capLevel
      ? formatCurrency(teamInvitationData.capLevel)
      : null;

    // Calculate team fees
    let calculatedTeamFees: {
      cap?: string;
      preCap?: string;
      postCap?: string;
    } | null = null;

    if (teamInvitationData.teamFeeCap) {
      calculatedTeamFees = {
        cap:
          teamInvitationData.teamFeeCap.capAmount === -1
            ? 'Unlimited'
            : formatCurrency(teamInvitationData.teamFeeCap.capAmount || 0),
        ...(teamInvitationData.teamFeeCap.preCapFee && {
          preCap: formatCurrency(teamInvitationData.teamFeeCap.preCapFee),
        }),
        ...(teamInvitationData.teamFeeCap.postCapFee && {
          postCap: formatCurrency(teamInvitationData.teamFeeCap.postCapFee),
        }),
      };
    }

    return {
      individualFees: calculatedIndividualFees,
      leaders: calculatedLeaders,
      teammateCap: calculatedTeammateCap,
      teamFees: calculatedTeamFees,
    };
  }, [teamInvitationData]);

  const shouldShowFixedFees = !!teamFees?.preCap || !!teamFees?.postCap;

  return (
    <div
      className='w-full flex flex-col space-y-4'
      data-testid='team-agreement'
    >
      {/* Teammate's Real Cap */}
      {teammateCap && (
        <TeamAgreementCard>
          <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
            <div className='flex items-center space-x-1'>
              <span className='font-inter font-medium text-base text-real-blue-950'>
                Teammate&apos;s Real Cap
              </span>
              <TooltipIcon content={TOOLTIP_CONTENT.TEAMMATE_REAL_CAP} />
            </div>
            <span
              className='font-inter font-medium text-base text-real-grey-600'
              data-testid='teammate-real-cap-value'
            >
              {teammateCap}
            </span>
          </div>
        </TeamAgreementCard>
      )}

      {/* Individual Fee Section */}
      <TeamAgreementCard>
        <div
          className='flex flex-col space-y-4'
          data-testid='individual-fee-section'
        >
          <div className='space-y-1'>
            <h2 className='font-inter font-medium text-base text-real-blue-950'>
              Individual Fee
            </h2>
            <p className='font-inter text-sm text-reskin-grey-600'>
              This is the percentage of each fee you&apos;ll pay Real.
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4'>
            <div className='flex flex-col space-y-1'>
              <div className='flex items-center space-x-1'>
                <span className='font-inter font-medium text-sm text-real-blue-950'>
                  Split
                </span>
                <TooltipIcon content={TOOLTIP_CONTENT.REAL_SPLIT} />
              </div>
              <span
                className='font-inter text-sm text-real-grey-600'
                data-testid='individual-split-value'
              >
                {individualFees.split}
              </span>
            </div>

            <div className='flex flex-col space-y-1'>
              <div className='flex items-center space-x-1'>
                <span className='font-inter font-medium text-sm text-real-blue-950'>
                  CBR
                </span>
                <TooltipIcon content={TOOLTIP_CONTENT.CBR_FEE} />
              </div>
              <span
                className='font-inter text-sm text-real-grey-600'
                data-testid='individual-cbr-value'
              >
                {individualFees.cbr}
              </span>
            </div>

            <div className='flex flex-col space-y-1'>
              <div className='flex items-center space-x-1'>
                <span className='font-inter font-medium text-sm text-real-blue-950'>
                  Brokerage
                </span>
                <TooltipIcon content={TOOLTIP_CONTENT.BROKERAGE_FEE} />
              </div>
              <span
                className='font-inter text-sm text-real-grey-600'
                data-testid='individual-brokerage-value'
              >
                {individualFees.brokerage}
              </span>
            </div>

            <div className='flex flex-col space-y-1'>
              <div className='flex items-center space-x-1'>
                <span className='font-inter font-medium text-sm text-real-blue-950'>
                  Post Cap Trx.
                </span>
                <TooltipIcon content={TOOLTIP_CONTENT.POST_CAP_TRX} />
              </div>
              <span
                className='font-inter text-sm text-real-grey-600'
                data-testid='individual-post-cap-value'
              >
                {individualFees.postCapTransaction}
              </span>
            </div>
          </div>
        </div>
      </TeamAgreementCard>

      {/* Leader's Fees Section */}
      {leaders.length > 0 && (
        <TeamAgreementCard>
          <div
            className='flex flex-col space-y-3'
            data-testid='leaders-fees-section'
          >
            <h2 className='font-inter font-medium text-base text-real-blue-950'>
              Leader&apos;s Fees
            </h2>
            {leaders.map((leader) => (
              <div
                key={leader.id}
                className='flex flex-col sm:flex-row sm:items-center gap-2'
                data-testid={`leader-${leader.id}`}
              >
                <span
                  className='font-inter text-sm text-real-blue-950'
                  data-testid={`leader-name-${leader.id}`}
                >
                  Leader: {leader.name}
                </span>
                <div className='flex items-center space-x-1'>
                  <span className='font-inter text-sm text-real-blue-950'>
                    Split
                  </span>
                  <TooltipIcon content={TOOLTIP_CONTENT.LEADER_SPLIT} />
                  <span
                    className='font-inter text-sm text-real-grey-600'
                    data-testid={`leader-split-${leader.id}`}
                  >
                    {leader.splitPercent}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </TeamAgreementCard>
      )}

      {/* Team's Fees Section */}
      {teamFees && (
        <TeamAgreementCard>
          <div
            className='flex flex-col space-y-4'
            data-testid='team-fees-section'
          >
            <h2 className='font-inter font-medium text-base text-real-blue-950'>
              Team&apos;s Fees
            </h2>

            {teamFees.cap && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
                <div className='flex items-center space-x-1'>
                  <span className='font-inter text-sm text-real-blue-950'>
                    Team Cap
                  </span>
                  <TooltipIcon content={TOOLTIP_CONTENT.TEAM_CAP} />
                </div>
                <span
                  className='font-inter text-sm text-real-grey-600'
                  data-testid='team-cap-value'
                >
                  {teamFees.cap}
                </span>
              </div>
            )}

            {shouldShowFixedFees && (
              <div>
                <p className='font-inter font-medium text-sm text-real-blue-950 mb-2'>
                  Fixed Fees:
                </p>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4'>
                  {teamFees.preCap && (
                    <div className='flex flex-col space-y-1'>
                      <div className='flex items-center space-x-1'>
                        <span className='font-inter text-sm text-real-blue-950'>
                          Team Pre Cap
                        </span>
                        <TooltipIcon content={TOOLTIP_CONTENT.TEAM_PRE_CAP} />
                      </div>
                      <span
                        className='font-inter text-sm text-real-grey-600'
                        data-testid='team-pre-cap-value'
                      >
                        {teamFees.preCap}
                      </span>
                    </div>
                  )}

                  {teamFees.postCap && (
                    <div className='flex flex-col space-y-1'>
                      <div className='flex items-center space-x-1'>
                        <span className='font-inter text-sm text-real-blue-950'>
                          Team Post Cap
                        </span>
                        <TooltipIcon content={TOOLTIP_CONTENT.TEAM_POST_CAP} />
                      </div>
                      <span
                        className='font-inter text-sm text-real-grey-600'
                        data-testid='team-post-cap-value'
                      >
                        {teamFees.postCap}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </TeamAgreementCard>
      )}

      {/* Empty state */}
      {!teamInvitationData && (
        <div className='w-full p-6 bg-gray-50 border border-gray-200 rounded-lg'>
          <p className='font-inter text-sm text-gray-700'>
            No fee information available.
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamAgreement;
